<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :tabs="tabs"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :selectedIndex="selectedIndex"
    :baseView="baseView"
    :formsConfig="formsConfig"
    :key="pageKey"
    :hot-key-create-path="`/supplier-contact/create/${this.baseView.id}`"
    :hot-key-grid-tab-index="1"
    :hot-key-create-route-id="model.SupplierCode"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Supplier"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="model.SupplierName"
        :secondaryLabel="model.SupplierCode"
        recordType="Supplier"
        v-if="model.SupplierCode"
      />
    </template>
    <div class="css-v2 split-view">
      <gc-split-view
        :showLeftContainer="showLeftContainer"
        :showRightContainer="showRightContainer"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <gc-filter-split-view
              :showLeftContainer="showFilterLeftContainer"
              :showRightContainer="showFilterRightContainer"
            >
              <template #leftContent>
                <dx-data-grid
                  :data-source="dataSourceConfig"
                  ref="contactGrid"
                  :columns="columns"
                  :height="gridHeight - 20"
                  @selection-changed="onSelectionChanged"
                  @toolbar-preparing="onToolbarPreparing($event)"
                  @content-ready="addGridNoResults($event)"
                  v-if="model.SupplierCode"
                  :selection="{ mode: 'single' }"
                  @exporting="
                    onGridToExcelExporting(
                      $event,
                      `Supplier-${baseView.id} Contact List`,
                      columns
                    )
                  "
                  :row-alternation-enabled="true"
                >
                  <DxPaging :page-size="50" :enabled="false" />
                  <DxPager
                    :showInfo="true"
                    :showPageSizeSelector="false"
                    :infoText="'({2} items)'"
                    :visible="false"
                  />

                  <DxScrolling
                    :mode="'virtual'"
                    :row-rendering-mode="'virtual'"
                    :preloadEnabled="true"
                  />

                  <DxExport :enabled="true" />
                  <template #active-cell-template="{ data }">
                    <GcInactive :inactive="data.data.Inactive" />
                  </template>
                  <template #email-purchase-order-cell-template="{ data }">
                    <DxCheckBox
                      :value="data.data.EmailPurchaseOrder == 'Y'"
                      :read-only="true"
                    />
                  </template>
                  <template #email-remittance-cell-template="{ data }">
                    <DxCheckBox
                      :value="data.data.EmailRemittance == 'Y'"
                      :read-only="true"
                    />
                  </template>
                  <template #email-rcti-cell-template="{ data }">
                    <DxCheckBox
                      :value="data.data.EmailRCTI == 'Y'"
                      :read-only="true"
                    />
                  </template>
                  <template #email-supplier-credit-cell-template="{ data }">
                    <DxCheckBox
                      :value="data.data.EmailSupplierCredit == 'Y'"
                      :read-only="true"
                    />
                  </template>

                  <template #name-cell-template="{ data }">
                    <span
                      >{{ data.data.FirstName }} {{ data.data.Surname }}</span
                    >
                  </template>
                </dx-data-grid>
              </template>
              <template #rightContent>
                <gc-advance-filter-panel
                  :toolbar="filterToolbar"
                  :setFilterFormHeight="filterFormHeight"
                  :sideBarTabs="sideBarTabs"
                  :filterFormItems="filterFormItems"
                  :filterModel="getAdvanceFilterModel"
                  :isJson="false"
                  :screenType="'supplier-contact-list'"
                  :data-source-config="dataSourceConfig"
                  :initial-filter="initialFilter"
                  v-if="model.SupplierCode"
                >
                </gc-advance-filter-panel>
              </template>
            </gc-filter-split-view>
          </div>
        </template>

        <template #rightContent>
          <DxToolbar class="split-view-toolbar">
            <DxItem #default location="before" locate-in-menu="never">
              <div class="toolbar-label">
                <span class="text-primary"
                  >{{ model.FirstName }} {{ model.Surname }}</span
                >
              </div>
            </DxItem>
            <DxItem
              widget="dxForm"
              location="after"
              :options="{
                formData: model,
                items: toolbarActiveItems,
                validationGroup: 'toolbarActive',
                labelLocation: 'left',
              }"
              locate-in-menu="after"
              :disabled="baseView.mode == 'review' ? true : false"
            ></DxItem>
            <DxItem
              :options="toolbar.audit"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              v-if="baseView.mode == 'review'"
            />
            <DxItem
              :options="toolbar.edit"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              v-if="baseView.mode == 'review'"
              :visible="formsConfig.editableOption"
            />

            <DxItem
              :options="toolbar.save"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              v-if="baseView.mode != 'review'"
            />

            <DxItem
              :options="toolbar.close"
              location="after"
              widget="dxButton"
            />
          </DxToolbar>
          <div class="view-card responsive-paddings">
            <form>
              <DxForm
                :form-data.sync="model"
                :read-only="false"
                :show-colon-after-label="false"
                :show-required-mark="false"
                :show-validation-summary="false"
                :validation-group="'supplier-contact-form'"
                :col-count-by-screen="formsConfig.splitViewColCountByScreen"
                label-location="left"
                :customizeItem="customizeItem"
                :items="items"
              ></DxForm>
            </form>
          </div>
        </template>
      </gc-split-view>
    </div>
  </gc-main-layout>
</template>

<script>
import BaseViewMixin from "../../v1/_mixins/base.view";
import validationEngine from "devextreme/ui/validation_engine";
import { SupplierService } from "../../_services/api/suppliers/supplier.service";
import {
  SupplierContactService,
  SupplierContactTransactionDistributionService,
} from "../../_services/api/suppliers/supplier-contact.service";
import {
  SupplierDetailTabs,
  SupplierDetailTabsVisibility,
  IsGrowerTabVisible,
} from "../../_models/tabdata/suppliers/supplier-detail-tabs";

export default {
  components: {},
  mixins: [BaseViewMixin],
  updated() {
    if (this.showRightContainer) {
      validationEngine.validateGroup(this.formsConfig.validationGroup);
      validationEngine.validateGroup("toolbarActive");
    }
  },
  created() {
    this.selectedIndex = 1;
  },
  data() {
    return {
      toolbar: {
        close: {
          icon: "close",
          onClick: this.close,
        },
        edit: {
          icon: "edit",

          onClick: () => {
            this.$router.push({
              path: `/supplier-contact/edit/${this.baseView.id}`,
            });
          },
        },
        save: {
          icon: "floppy",
          validationGroup: "formsData",
          onClick: (e) => {
            this.$logger.trackEventButtonClickSave();
            this.saveSupplierContact(e);
          },
        },
        audit: {
          icon: "clock",

          onClick: () => {
            this.displayAuditPopup(
              this.model.CreatedBy,
              this.model.DateCreated,
              this.model.ChangedBy,
              this.model.DateChanged
            );
          },
        },
      },
      advanceFilterModel: {
        Inactive: "",
        EmailPurchaseOrder: "",
      },
      model: {
        SupplierCode: "",
        SupplierName: "",
        DateChanged: "",
        DateCreated: "",
        ChangedBy: "",
        CreatedBy: "",
        RecordId: "00000000-0000-0000-0000-000000000000",
        LineNo: 0,
        FirstName: "",
        Surname: "",
        Department: "",
        Position: "",
        Email: "",
        Phone: "",
        Mobile: "",
        Fax: "",
        EmailPurchaseOrder: false,
        EmailRemittance: false,
        EmailRCTI: false,
        EmailSupplierCredit: false,
        Active: true,
      },
      noDataMessage: {
        addNew: {
          class: "add-new",
          title: "No Contacts",
          message: "Get Started, Add a Contact",
        },
      },
    };
  },
  computed: {
    initialFilter() {
      return [["SupplierCode", "=", this.model.SupplierCode]];
    },
    getAdvanceFilterModel() {
      return this.advanceFilterModel;
    },
    filterFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: this.parseFormColumns([
            {
              dataField: "EmailPurchaseOrder",
              gcType: "String",
              editorType: "dxTagBox",
              gcLabel: "Email Purchase Order",
              editorOptions: {
                items: [
                  {
                    text: "Yes",
                    value: "Y",
                  },
                  {
                    text: "No",
                    value: "N",
                  },
                ],
                valueExpr: "value",
                displayExpr: "text",
              },
            },
            {
              dataField: "Inactive",
              gcType: "String",
              editorType: "dxTagBox",
              gcLabel: "Status",
              editorOptions: {
                items: [
                  {
                    text: "Active",
                    value: "N",
                  },
                  {
                    text: "Inactive",
                    value: "Y",
                  },
                ],
                valueExpr: "value",
                displayExpr: "text",
              },
            },
          ]),
        },
      ];
    },
    columns() {
      return this.parseGridColumns("master", [
        {
          gcLabel: "Name",
          dataField: "FirstName",
          cellTemplate: "name-cell-template",
          gcType: "String",
          gcSubType: "PrimaryRecord",
          gcExport: ["FirstName", "Surname"],
        },
        {
          gcLabel: "Department",
          dataField: "Department",
          gcType: "String",
          width: 220,
        },
        {
          gcLabel: "Position",
          dataField: "Position",
          gcType: "String",
          gcSubType: "HiddenDescription",
          width: 120,
        },
        {
          gcLabel: "Phone",
          dataField: "Phone",
          gcType: "String",
          gcSubType: "HiddenDescription",
          width: 120,
        },
        {
          gcLabel: "Mobile",
          dataField: "Mobile",
          gcType: "String",
          width: 220,
        },
        {
          gcLabel: "Email",
          dataField: "Email",
          gcType: "String",
          width: 220,
        },
        {
          gcType: "String",
          gcSubType: "Chip",
          dataField: "Inactive",
          cellTemplate: "active-cell-template",
        },
        {
          gcLabel: "First Name",
          dataField: "FirstName",
          width: 120,
          gcType: "String",
          gcSubType: "HiddenCode",
        },
        {
          gcLabel: "Surname",
          dataField: "Surname",
          width: 120,
          gcType: "String",
          gcSubType: "HiddenDescription",
        },
        {
          dataField: "EmailPurchaseOrder",
          gcLabel: "Email Purchase Order",
          gcType: "String",
          gcSubType: "HiddenDescription",
          width: 100,
          cellTemplate: "email-purchase-order-cell-template",
        },
        {
          dataField: "EmailRCTI",
          gcLabel: "Email Invoice / RCTI",
          gcType: "String",
          gcSubType: "HiddenDescription",
          width: 100,
          cellTemplate: "email-rcti-cell-template",
        },
        {
          dataField: "EmailRemittance",
          gcLabel: "Email Remittance",
          gcType: "String",
          gcSubType: "HiddenDescription",
          width: 100,
          cellTemplate: "email-remittance-cell-template",
        },
        {
          dataField: "EmailSupplierCredit",
          gcLabel: "Email Supplier Credit",
          gcType: "String",
          gcSubType: "HiddenDescription",
          width: 100,
          cellTemplate: "email-supplier-credit-cell-template",
        },
      ]);
    },
    items() {
      let formFields = [
        {
          itemType: "group",
          caption: "",
          items: this.parseFormColumns([
            {
              dataField: "FirstName",
              gcLabel: "First Name",
              gcType: "String",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 50,
              },
              required: true,
            },
            {
              dataField: "Surname",
              gcLabel: "Surname",
              gcType: "String",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 50,
              },
              required: true,
            },
            {
              dataField: "Department",
              gcLabel: "Department",
              gcType: "String",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 100,
              },
            },
            {
              dataField: "Position",
              gcLabel: "Position",
              gcType: "String",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 15,
              },
            },
            {
              dataField: "Email",
              gcLabel: "Email",
              gcType: "String",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 200,
              },
              validationRules: [
                {
                  type: "email",
                  message: "Email is invalid",
                },
                {
                  type: "custom",
                  message:
                    "Email is required when 1 or more email options is On",
                  validationCallback: this.emailValidator,
                  // reevaluate: true,
                },
              ],
            },
            {
              dataField: "Phone",
              gcLabel: "Phone",
              gcType: "String",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 20,
              },
            },

            {
              dataField: "Mobile",
              gcLabel: "Mobile",
              gcType: "String",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 20,
              },
            },
            {
              dataField: "EmailPurchaseOrder",
              gcLabel: "Email Purchase Order",
              gcType: "Boolean",
              editorOptions: {
                readOnly: !this.model.Email,
              },
            },
            {
              dataField: "EmailRCTI",
              gcLabel: "Email Invoice / RCTI",
              gcType: "Boolean",
              editorOptions: {
                readOnly: !this.model.Email,
              },
            },
            {
              dataField: "EmailRemittance",
              gcLabel: "Email Remittance",
              gcType: "Boolean",
              editorOptions: {
                readOnly: !this.model.Email,
              },
            },
            {
              dataField: "EmailSupplierCredit",
              gcLabel: "Email Supplier Credit",
              gcType: "Boolean",
              editorOptions: {
                readOnly: !this.model.Email,
              },
            },
          ]),
        },
      ];

      return formFields;
    },
    toolbarActiveItems() {
      return this.parseFormColumns([
        {
          dataField: "Active",
          gcType: "Boolean",
          gcLabel: "Active",
        },
      ]);
    },
    dataSourceConfig() {
      return SupplierContactService.dataSourceConfig(
        "api/SupplierContactList",
        {
          select: [
            "SupplierCode,RecordId,LineNo,FirstName,Surname,Department,Position,Phone,Email,Mobile,Inactive,EmailPurchaseOrder,EmailRemittance,EmailRCTI,EmailSupplierCredit",
          ],
          // sort: [{ selector: "FirstName", desc: false }],
          filter: [["SupplierCode", "=", this.model.SupplierCode]],
        }
      );
    },
    tabs() {
      // Almondco - 2116 - Grower Master: repointed from an inline array to
      // the shared tab-data module (also fixes the pre-existing Transactions
      // link, which pointed at baseView.id - the contact's RecordId in
      // review/edit mode, not the Supplier code the other tabs used).
      // Gating on loadingCompleted is required for the Grower visibility
      // flag set in handleLocalRouteChange() to ever reach the rendered
      // tab strip - see supplier-detail.vue's tabs() for why.
      if (this.loadingCompleted) return SupplierDetailTabs(this.model.SupplierCode);
      else return [];
    },
    showLeftContainer() {
      return !(this.isXSmall && this.baseView.mode !== "");
    },
    showRightContainer() {
      if (
        (this.baseView.mode == "review" || this.baseView.mode == "edit") &&
        this.baseView.id.length < 36
      ) {
        return false;
      }

      return !(this.baseView.mode == "review" && this.baseView.id == "");
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler: "handleLocalRouteChange",
    },
    showFilterRightContainer() {
      setTimeout(() => {
        let grid = this.$refs["contactGrid"].instance;
        grid.updateDimensions();
      }, 10);
    },
  },
  methods: {
    emailValidator(options) {
      return !(
        options.value == "" &&
        (this.model.EmailPurchaseOrder ||
          this.model.EmailRemittance ||
          this.model.EmailRCTI ||
          this.model.EmailSupplierCredit)
      );
    },
    emailSwitchValidator(options) {
      if (!options.value) {
        return true;
      } else {
        return this.model.Email != "";
      }
    },
    async close() {
      if (this.baseView.mode == "create" || this.baseView.mode == "edit") {
        let confirmLeaving = await this.$confirm(
          "You have unsaved changes. Are you sure you want to leave?",
          "Unsaved changes"
        );

        if (confirmLeaving) {
          let grid = this.$refs["contactGrid"].instance;
          grid.clearSelection();

          this.baseView.id = this.model.SupplierCode;
          this.setIgnoreRouteChangeConfirmation();
          this.$router
            .push({
              path: `/supplier-contact/${this.baseView.id}`,
            })
            .then(() => {
              let grid = this.$refs["contactGrid"].instance;
              grid.updateDimensions();
            });
        } else {
          this.next(false);
        }
      } else {
        let grid = this.$refs["contactGrid"].instance;
        grid.clearSelection();

        this.baseView.id = this.model.SupplierCode;
        this.setIgnoreRouteChangeConfirmation();
        this.$router
          .push({
            path: `/supplier-contact/${this.baseView.id}`,
          })
          .then(() => {
            grid.updateDimensions();
          });
      }
    },
    backButtonFunction() {
      this.$logger.debug(`Form dirty:  ${this.dirty}`);
      if (this.isDirty == false) {
        this.setIgnoreRouteChangeConfirmation();
      }
      if (this.baseView.query && this.baseView.query.from) {
        this.$router.push({
          path: this.baseView.query.from,
        });
      } else
        this.$router.push({
          path: "/supplier",
        });
    },
    onToolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });

      var columnChooserButton = toolbarItems.find((item) => {
        return item.name === "columnChooserButton";
      });

      var exportButton = toolbarItems.find((item) => {
        return item.name === "exportButton";
      });

      e.toolbarOptions.items = toolbarItems.filter((item) => {
        return (
          item.name !== "searchPanel" &&
          item.name !== "columnChooserButton" &&
          item.name !== "exportButton"
        );
      });

      searchPanel.location = "after";
      exportButton.location = "after";

      e.toolbarOptions.items.unshift(
        {
          location: "before",
          widget: "dxButton",
          options: {
            icon: "plus",
            text: "Add New",
            onClick: () => {
              this.$logger.trackEventButtonClickAddNew();
              this.model.RecordId = "00000000-0000-0000-0000-000000000000";
              this.model.DateChanged = "";
              this.model.DateCreated = "";
              this.model.ChangedBy = "";
              this.model.CreatedBy = "";
              this.model.LineNo = 0;
              this.model.FirstName = "";
              this.model.Surname = "";
              this.model.Department = "";
              this.model.Position = "";
              this.model.Email = "";
              this.model.Phone = "";
              this.model.Mobile = "";
              this.model.Fax = "";
              this.model.EmailPurchaseOrder = false;
              this.model.EmailRemittance = false;
              this.model.EmailRCTI = false;
              this.model.EmailSupplierCredit = false;
              this.model.Active = true;
              this.baseView.id = this.model.SupplierCode;

              this.$router.push({
                path: `/supplier-contact/create/${this.baseView.id}`,
              });
            },
          },
        },
        searchPanel,
        exportButton,
        columnChooserButton,
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            validationGroup: "formsData",
            icon: "filter",
            elementAttr: { id: "filter-btn" },
            onClick: () => {
              this.showFilterRightContainer = !this.showFilterRightContainer;
              var item = document.getElementById("filter-btn");
              this.$logger.debug("item ", item);
              item.classList.add("dx-state-invisible");
            },
          },
        }
      );
    },
    handleLocalRouteChange() {
      let that = this;
      if (
        (this.baseView.mode === "review" || this.baseView.mode === "edit") &&
        this.baseView.id.length == 36
      ) {
        this.model.RecordId = this.baseView.id;
        this.getRecord().then(async (response) => {
          if (response.data.value.length > 0) {
            this.model.RecordId = response.data.value[0].RecordId;
            this.model.DateChanged = response.data.value[0].DateChanged;
            this.model.DateCreated = response.data.value[0].DateCreated;
            this.model.ChangedBy = response.data.value[0].ChangedBy;
            this.model.CreatedBy = response.data.value[0].CreatedBy;
            this.model.LineNo = response.data.value[0].LineNo;
            this.model.SupplierCode = response.data.value[0].SupplierCode;
            this.model.SupplierName = response.data.value[0].Supplier.Name;
            this.model.FirstName = response.data.value[0].FirstName;
            this.model.Surname = response.data.value[0].Surname;
            this.model.Department = response.data.value[0].Department;
            this.model.Position = response.data.value[0].Position;
            this.model.Email = response.data.value[0].Email;
            this.model.Phone = response.data.value[0].Phone;
            this.model.Mobile = response.data.value[0].Mobile;
            this.model.Fax = response.data.value[0].Fax;
            this.model.Active = response.data.value[0].Inactive == "N";

            // Must be awaited before loadingCompleted flips to true below -
            // tabs() only re-evaluates when loadingCompleted actually
            // changes, so setting it before this resolves would lock in
            // the stale/default visibility value permanently.
            await IsGrowerTabVisible(
              response.data.value[0].Supplier.SupplierTypeCode
            ).then((visible) => {
              SupplierDetailTabsVisibility({ text: "Grower", value: visible });
            });

            SupplierContactTransactionDistributionService.getBySupplierCodeLineNo(
              this.model.SupplierCode,
              this.model.LineNo
            ).then((response2) => {
              if (response2.data.value.length > 0) {
                this.model.EmailPurchaseOrder =
                  response2.data.value.find(
                    (element) => element.TransactionCode == "PURCH"
                  ) != null;
                this.model.EmailRemittance =
                  response2.data.value.find(
                    (element) => element.TransactionCode == "CPAY"
                  ) != null;
                this.model.EmailRCTI =
                  response2.data.value.find(
                    (element) => element.TransactionCode == "SUPINV"
                  ) != null;
                this.model.EmailSupplierCredit =
                  response2.data.value.find(
                    (element) => element.TransactionCode == "SUPCRED"
                  ) != null;
              } else {
                this.model.EmailPurchaseOrder = false;
                this.model.EmailRemittance = false;
                this.model.EmailRCTI = false;
                this.model.EmailSupplierCredit = false;
              }
            });
          }
          // Pre-existing gap: this branch never set loadingCompleted, which
          // was harmless while tabs() didn't depend on it - now that tabs()
          // is gated on it (required for Grower visibility to propagate),
          // reviewing a specific contact would otherwise show zero tabs.
          that.loadingCompleted = true;
        });
      } else {
        if (!that.model.SupplierCode) {
          SupplierService.getByCode(that.baseView.id).then(async (response) => {
            if (response.data.value.length > 0) {
              that.model.SupplierCode = response.data.value[0].Code;
              that.model.SupplierName = response.data.value[0].Name;
              // that.refreshView();
              await IsGrowerTabVisible(
                response.data.value[0].SupplierTypeCode
              ).then((visible) => {
                SupplierDetailTabsVisibility({
                  text: "Grower",
                  value: visible,
                });
              });
            }
            that.loadingCompleted = true;
          });
        } else {
          if (this.showRightContainer) this.loadingCompleted = true;
        }
      }
    },
    async getRecord() {
      return SupplierContactService.getByRecordId(this.baseView.id);
    },
    onSelectionChanged({ selectedRowsData }) {
      if (selectedRowsData.length == 0) {
        return;
      }

      this.baseView.id = selectedRowsData[0].RecordId;
      this.$router
        .push({
          path: `/supplier-contact/review/${selectedRowsData[0].RecordId}`,
        })
        .then(() => {
          let grid = this.$refs["contactGrid"].instance;
          grid.updateDimensions();
        });
    },

    async deleteSupplierContact() {
      let that = this;

      let confirmLeaving = await this.$confirm(
        `Are you sure you want to delete ${this.model.FirstName}?`,
        "Delete Contact"
      );

      if (confirmLeaving) {
        this.showLoading(true);
        await SupplierContactService.delete({
          RecordId: this.model.RecordId,
        })
          // eslint-disable-next-line no-unused-vars
          .then((res) => {
            that.showLoading(false);
            that.close();
            let grid = this.$refs["contactGrid"].instance;
            grid.refresh();
          })
          .catch((error) => {
            this.displayServerError(error, "Supplier Contact delete failed");
          });
      }
    },
    async saveSupplierContact() {
      const mainForm = await validationEngine.validateGroup(
        "supplier-contact-form"
      );

      if (!mainForm.isValid) {
        this.addErrorMessagesFromValidationResult(mainForm);
        this.displayValidationSummary();
        return;
      }
      this.showLoading(true);
      await SupplierContactService.save(this.baseView.mode, {
        SupplierCode: this.model.SupplierCode,
        Inactive: this.model.Active ? "N" : "Y",
        RecordId: this.model.RecordId,
        LineNo: this.model.LineNo,
        FirstName: this.model.FirstName,
        Surname: this.model.Surname,
        Department: this.model.Department,
        Position: this.model.Position,
        Email: this.model.Email,
        Phone: this.model.Phone,
        Mobile: this.model.Mobile,
        Fax: this.model.Fax,
        EmailPurchaseOrder: this.model.EmailPurchaseOrder ? "Y" : "N",
        EmailRemittance: this.model.EmailRemittance ? "Y" : "N",
        EmailRCTI: this.model.EmailRCTI ? "Y" : "N",
        EmailSupplierCredit: this.model.EmailSupplierCredit ? "Y" : "N",
      })
        .then((res) => {
          if (this.baseView.mode == "create") {
            this.model.RecordId = res.data.RecordId;
            this.model.LineNo = res.data.LineNo;
          }

          this.showLoading(false);
          // eslint-disable-next-line no-console
          // this.$logger.debug("saveSupplierContact", res);
          this.$notify(
            `Supplier Contact ${this.model.FirstName} saved successfully`,
            "success"
          );
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({
            path: `/supplier-contact/review/${this.model.RecordId}`,
          });
        })
        .catch((error) => {
          this.displayServerError(error, "Supplier Contact saved failed");
        });
    },
  },
};
</script>
